import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { SARABUN_REGULAR_B64, SARABUN_BOLD_B64 } from './fonts/sarabunFont'

/**
 * วางรูปที่ capture มา (เช่นจาก html2canvas) ลง PDF โดยแบ่งหน้าอัตโนมัติถ้าเนื้อหาสูงเกินหน้าเดียว
 * ตัด canvas ต้นฉบับเป็นชิ้นๆ ตามพื้นที่ที่เหลือในแต่ละหน้า แทนที่จะบีบทั้งภาพลงหน้าเดียว (ซึ่งจะเล็กจนอ่านไม่ออก)
 */
export function addCanvasWithPagination(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  startY: number,     // ตำแหน่ง y ที่จะเริ่มวางในหน้าแรก (ต่อจาก header)
  pageW: number,
  pageH: number,
  margin: number,
): void {
  const imgW = pageW - margin * 2
  const scale = imgW / canvas.width         // mm ต่อ 1px ของ canvas ต้นฉบับ
  const pxPerPageFirst = (pageH - startY - margin) / scale
  const pxPerPageRest  = (pageH - margin * 2) / scale

  let renderedPx = 0
  let pageNum = 0
  while (renderedPx < canvas.height) {
    const sliceH = pageNum === 0 ? pxPerPageFirst : pxPerPageRest
    const thisSliceH = Math.min(sliceH, canvas.height - renderedPx)
    if (thisSliceH <= 0) break

    const sliceCanvas = document.createElement('canvas')
    sliceCanvas.width = canvas.width
    sliceCanvas.height = thisSliceH
    const ctx = sliceCanvas.getContext('2d')
    if (ctx) {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height)
      ctx.drawImage(canvas, 0, renderedPx, canvas.width, thisSliceH, 0, 0, canvas.width, thisSliceH)
    }
    const sliceDataUrl = sliceCanvas.toDataURL('image/png')
    const sliceHmm = thisSliceH * scale

    if (pageNum > 0) pdf.addPage()
    const y = pageNum === 0 ? startY : margin
    pdf.addImage(sliceDataUrl, 'PNG', margin, y, imgW, sliceHmm)

    renderedPx += thisSliceH
    pageNum++
  }
}
export const DARK: [number,number,number] = [15, 23, 42]
export const GRAY: [number,number,number] = [100, 116, 139]
export const LGRAY: [number,number,number] = [226, 232, 240]

// ฟอนต์เดียวที่ใช้ทั่วทั้งระบบเอกสารพิมพ์ — ต้อง 'Sarabun' เสมอ ไม่ใช่ 'helvetica'
// เพราะ helvetica ไม่มีตัวอักษรไทยเลย (ปัญหาเดียวกับที่เจอตอนทำป้าย QR)
export const PDF_FONT = 'Sarabun'

/** ฝังฟอนต์ไทยเข้า jsPDF instance — ต้องเรียกครั้งเดียวหลังสร้าง pdf ใหม่ทุกครั้ง */
export function registerThaiFont(pdf: jsPDF): void {
  pdf.addFileToVFS('Sarabun-Regular.ttf', SARABUN_REGULAR_B64)
  pdf.addFont('Sarabun-Regular.ttf', PDF_FONT, 'normal')
  pdf.addFileToVFS('Sarabun-Bold.ttf', SARABUN_BOLD_B64)
  pdf.addFont('Sarabun-Bold.ttf', PDF_FONT, 'bold')
}

/** หาสัดส่วนจริงของรูป (width/height) จาก base64 — ใช้คำนวณ auto-fit ไม่ให้โลโก้บิดเบี้ยว */
export function getImageNaturalSize(base64: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 })
    img.onerror = () => resolve({ w: 1, h: 1 })
    img.src = base64
  })
}

/**
 * เลือกขนาดฟอนต์ที่ใหญ่ที่สุดที่ยังพอดีกับความกว้างที่กำหนด — กัน text ตกกรอบเด็ดขาด
 * ไม่ว่าชื่อรายงานจะสั้นยาวแค่ไหน หรือเป็นภาษาไทยที่กว้างกว่าอังกฤษ
 */
export function fitFontSize(pdf: jsPDF, text: string, maxWidth: number, startSize: number, minSize: number, style: 'bold' | 'normal' = 'bold'): number {
  pdf.setFont(PDF_FONT, style)
  let size = startSize
  while (size > minSize) {
    pdf.setFontSize(size)
    if (pdf.getTextWidth(text) <= maxWidth) return size
    size -= 0.5
  }
  return minSize
}

/**
 * แปลง image URL → base64 สำหรับ jsPDF
 * ถ้าโหลดไม่ได้ คืน null (ไม่แสดง logo)
 */
export async function urlToBase64(url: string): Promise<string | null> {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

/**
 * แปลง element ในหน้าเว็บ (กราฟ + ตาราง) ให้เป็น PDF จริงที่:
 * - กราฟ/การ์ดสรุป → capture เป็นภาพวางไว้บนสุด (recharts วาดเป็น PDF element ตรงๆ ไม่ได้)
 * - ตาราง <table> ใดๆ ที่เจอในเนื้อหา → ดึงข้อมูลจริงมาสร้างเป็นตาราง PDF จริงด้วย jspdf-autotable
 *   ซึ่ง "หัวตารางซ้ำอัตโนมัติทุกหน้า" มาพร้อมของปลั๊กอินอยู่แล้ว ไม่ต้องเขียนเอง —
 *   แก้ปัญหาที่วิธีเดิม (capture ทั้งหน้าเป็นรูปเดียวแล้วตัดหน้า) ทำไม่ได้เลยโดยธรรมชาติ
 * ตัวอักษรในตารางบังคับเป็นสีดำล้วนตามที่ขอ ไม่ใช้สีเทาอ่านยาก, หัวตารางเป็นแถบสีทึบชัดเจน
 */
export async function renderReportPdf(
  pdf: jsPDF,
  el: HTMLElement,
  startY: number,
  pageW: number,
  pageH: number,
  margin: number,
): Promise<void> {
  let y = startY

  // ── แยกส่วน "กราฟ/การ์ด" ออกจาก "ตาราง" — clone มาตัดตารางออก เหลือไว้แค่กราฟ ──
  const clone = el.cloneNode(true) as HTMLElement
  const tablesInClone = Array.from(clone.querySelectorAll('table'))
  tablesInClone.forEach(t => t.remove())

  const hasNonTableContent = clone.textContent && clone.textContent.trim().length > 0
  if (hasNonTableContent) {
    // ต้องแปะ clone ไว้ในหน้าจอจริงชั่วคราว (นอกจอ) ให้ html2canvas วัด layout ได้ถูกต้อง
    clone.style.position = 'fixed'
    clone.style.left = '-9999px'
    clone.style.top = '0'
    clone.style.width = `${el.offsetWidth || 900}px`
    document.body.appendChild(clone)
    try {
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(clone, { scale: 2, backgroundColor: '#ffffff', useCORS: true })
      if (canvas.height > 4) {  // เผื่อกรณีเนื้อหาว่างจริงๆ (สูงแค่ไม่กี่ px)
        addCanvasWithPagination(pdf, canvas, y, pageW, pageH, margin)
        // หาตำแหน่ง y ปัจจุบันหลังวางภาพ (หน้าสุดท้ายที่ addCanvasWithPagination ใช้)
        const imgW = pageW - margin * 2
        const scale = imgW / canvas.width
        y = margin + (canvas.height * scale) % (pageH - margin * 2)
        if (y < margin + 10) y = margin  // ถ้าพอดีจบหน้าเป๊ะ ให้ตารางเริ่มหน้าใหม่
      }
    } finally {
      document.body.removeChild(clone)
    }
  }

  // ── ตาราง — ดึงจาก DOM จริง (ไม่ใช่ clone) มาสร้างเป็น autotable จริง ──
  const tables = Array.from(el.querySelectorAll('table'))
  for (const table of tables) {
    const head: string[][] = []
    const headRow = table.querySelector('thead tr')
    if (headRow) head.push(Array.from(headRow.children).map(c => (c.textContent ?? '').trim()))

    const body: string[][] = []
    const bodyRows = table.querySelectorAll('tbody tr')
    bodyRows.forEach(tr => {
      body.push(Array.from(tr.children).map(td => (td.textContent ?? '').trim()))
    })
    if (body.length === 0) continue

    autoTable(pdf, {
      head, body,
      startY: y,
      margin: { left: margin, right: margin },
      styles: { font: PDF_FONT, fontSize: 8, textColor: [0, 0, 0], lineColor: [226, 232, 240], lineWidth: 0.1 },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'left' },
      bodyStyles: { textColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      // ── หัวตารางซ้ำทุกหน้าโดยอัตโนมัติ — เป็นพฤติกรรม default ของปลั๊กอินนี้อยู่แล้ว ──
      showHead: 'everyPage',
    })
    // @ts-ignore — jspdf-autotable แนบ lastAutoTable ไว้ที่ instance หลังเรียกเสร็จ
    y = (pdf as any).lastAutoTable.finalY + 10
  }
}
export async function drawHeader(
  pdf: jsPDF,
  title: string,
  subtitle: string,
  ref: string,
  date: string,
  logoBase64?: string | null,  // ← รับ logo จาก Tenant (optional)
  companyName?: string | null, // ← ชื่อ company แสดงใต้ logo (optional)
  pageWidth: number = 210,     // ← 210 = A4 portrait (default เดิม), 297 = A4 landscape
  // 'dark' (ของเดิม) = กล่องพื้นเข้มตัวหนังสือขาว, ใช้กับรายงานส่วนใหญ่ (WriteOff/License/Maintenance ฯลฯ)
  // 'plain' = ตัวหนังสือดำล้วนบนพื้นขาว ไม่มีกล่องสี — ใช้กับ Transfer/Borrow ตามที่ขอให้เหมือนรายงานส่งมอบ
  style: 'dark' | 'plain' = 'dark',
  // ป้าย "Ref No." / "Date" — ค่าเริ่มต้นเดิมเป็นอังกฤษเสมอเพื่อไม่กระทบผู้เรียกเดิม ส่ง lang ไทยมาเมื่อต้องการ
  refDateLabels: { ref: string; date: string } = { ref: 'Ref No.', date: 'Date' },
): Promise<number> {
  const W = pageWidth, M = 15
  let y = M

  // ── Logo — กรอบเผื่อไว้กว้างๆ (โลโก้แนวนอน/แนวตั้ง/สี่เหลี่ยมก็พอดีได้หมด) ──
  // คำนวณสัดส่วนจริงของรูปก่อนวาง ไม่บีบเป็นสี่เหลี่ยมตายตัวเหมือนเดิม (ที่ทำให้โลโก้เสียรูป)
  const LOGO_BOX_W = 55, LOGO_BOX_H = 24  // พื้นที่เผื่อไว้สำหรับโลโก้ — ไม่ใช่ขนาดโลโก้จริง
  if (logoBase64) {
    try {
      const { w: natW, h: natH } = await getImageNaturalSize(logoBase64)
      const scale = Math.min(LOGO_BOX_W / natW, LOGO_BOX_H / natH)
      const drawW = natW * scale
      const drawH = natH * scale
      // จัดกึ่งกลางแนวตั้งในกรอบที่เผื่อไว้ ให้ดูเป็นธรรมชาติไม่ว่าโลโก้จะเป็นทรงไหน
      const drawY = y + (LOGO_BOX_H - drawH) / 2
      const ext = logoBase64.includes('image/png') ? 'PNG' : 'JPEG'
      pdf.addImage(logoBase64, ext, M, drawY, drawW, drawH, undefined, 'FAST')
    } catch (_) {
      if (companyName) {
        pdf.setFont(PDF_FONT, 'bold')
        pdf.setFontSize(12)
        pdf.setTextColor(15, 23, 42)
        pdf.text(companyName, M, y + 13)
        pdf.setTextColor(0, 0, 0)
      }
    }
  } else if (companyName) {
    pdf.setFont(PDF_FONT, 'bold')
    pdf.setFontSize(12)
    pdf.setTextColor(15, 23, 42)
    pdf.text(companyName, M, y + 13)
    pdf.setTextColor(0, 0, 0)
  }

  if (style === 'plain') {
    // ── หัวข้อตัวหนังสือดำล้วนบนพื้นขาว ชิดขวา — พอร์ตมาจาก Handover (PDFKit) เพื่อให้หน้าตาตรงกัน ──
    // boxW เดิมคำนวณผิด (W/2 - M) ทำให้ขอบขวาของกล่องอยู่ที่ W+4-M = 199mm เกินขอบกระดาษจริงที่ W-M=195mm
    // ไป 4mm — ข้อความชิดขวาจึงล้ำขอบขวาไปเล็กน้อย แก้ให้ตรงกับ margin ที่ใช้ทั้งเอกสาร (เหมือนสไตล์ dark)
    const boxX = W / 2 + 4
    const boxW = W / 2 - M - 4
    const textMaxW = boxW - 4
    const titleSize = fitFontSize(pdf, title, textMaxW, 15, 9, 'bold')
    pdf.setFont(PDF_FONT, 'bold')
    pdf.setFontSize(titleSize)
    pdf.setTextColor(0, 0, 0)
    pdf.text(title, boxX + boxW, subtitle ? y + 9 : y + 13, { align: 'right' })

    if (subtitle) {
      const subSize = fitFontSize(pdf, subtitle, textMaxW, 8, 6.5, 'normal')
      pdf.setFont(PDF_FONT, 'normal')
      pdf.setFontSize(subSize)
      pdf.setTextColor(100, 100, 100)
      pdf.text(subtitle, boxX + boxW, y + 15.5, { align: 'right' })
      pdf.setTextColor(0, 0, 0)
    }

    // ── เส้นคั่น — ต้องอยู่ใต้ "ทั้ง" หัวข้อ (ขวา) และโลโก้ (ซ้าย) เสมอ ──
    // ของเดิมคำนวณจากความสูงของหัวข้ออย่างเดียว (y+20≈35mm) โดยไม่เผื่อโลโก้เลย ถ้าโลโก้เป็นทรงสี่เหลี่ยม
    // สูงเต็มกรอบ (LOGO_BOX_H=24mm นับจาก M=15mm คือลึกถึง 39mm) เส้นจะตัดผ่านโลโก้พอดี — แก้ด้วยการหา
    // ตำแหน่งที่ต่ำที่สุดจากทั้งสองฝั่งก่อนค่อยวางเส้น
    const titleBlockBottom = M + (subtitle ? 19 : 17)
    const logoBlockBottom = M + LOGO_BOX_H
    y = Math.max(titleBlockBottom, logoBlockBottom) + 3
    pdf.setDrawColor(226, 232, 240)
    pdf.line(M, y, W - M, y)
    y += 7
  } else {
    // ── Dark title block — ฟอนต์ auto-fit เสมอ ไม่มีทางตกกรอบไม่ว่าชื่อรายงานจะยาวแค่ไหน ──
    const boxX = W / 2 + 4
    const boxW = W / 2 - M - 4
    const textMaxW = boxW - 8  // เผื่อ padding ซ้ายขวาข้างละ 4mm
    pdf.setFillColor(...DARK)
    pdf.rect(boxX, y, boxW, 22, 'F')
    pdf.setTextColor(255, 255, 255)

    const titleSize = fitFontSize(pdf, title, textMaxW, 12, 8, 'bold')
    pdf.setFont(PDF_FONT, 'bold')
    pdf.setFontSize(titleSize)
    pdf.text(title, boxX + 4, subtitle ? y + 8 : y + 12)

    if (subtitle) {
      const subSize = fitFontSize(pdf, subtitle, textMaxW, 7.5, 6, 'normal')
      pdf.setFont(PDF_FONT, 'normal')
      pdf.setFontSize(subSize)
      pdf.text(subtitle, boxX + 4, y + 15)
    }
    pdf.setTextColor(0, 0, 0)
    y += 27
  }

  // Ref + Date bar
  pdf.setFillColor(241, 245, 249)
  pdf.rect(M, y, W - M * 2, 8, 'F')
  pdf.setFont(PDF_FONT, 'bold')
  pdf.setFontSize(8.5)
  pdf.text(`${refDateLabels.ref}: ${ref}`, M + 3, y + 5.5)
  pdf.text(`${refDateLabels.date}: ${date}`, W - M - 3, y + 5.5, { align: 'right' })
  y += 13

  return y
}

/**
 * ตัดข้อความให้พอดีกับความกว้างที่กำหนด (มม.) โดยวัดความกว้างจริงของฟอนต์ ไม่ใช่นับจำนวนตัวอักษร
 *
 * ตารางรายการของ Transfer/Borrow เดิมตัดด้วย `.slice(0, 35)` ตายตัว ซึ่งเป็นการเดาความกว้างจาก
 * "จำนวนตัวอักษร" — ใช้ไม่ได้กับภาษาไทยที่แต่ละตัวอักษรกว้างไม่เท่ากัน (และกว้างกว่าอังกฤษโดยเฉลี่ย)
 * ข้อความยาวๆ จึงยังคงล้นออกไปติดขอบขวาของหน้าได้ (ปัญหาเดียวกับที่ Handover ไม่มีเพราะใช้ colW+width
 * ไปบังคับไว้ตั้งแต่แรก) ใช้ getTextWidth() วัดจริงแล้วตัดออกทีละตัวจนกว่าจะพอดี แม่นกว่าและใช้ได้ทั้ง 2 ภาษา
 */
export function truncateToWidth(pdf: jsPDF, text: string, maxWidth: number): string {
  if (!text) return text
  if (pdf.getTextWidth(text) <= maxWidth) return text
  const ellipsis = '…'
  let lo = 0, hi = text.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (pdf.getTextWidth(text.slice(0, mid) + ellipsis) <= maxWidth) lo = mid
    else hi = mid - 1
  }
  return text.slice(0, lo) + ellipsis
}

/**
 * Draw two-column row
 * ระยะเยื้องของ "ค่า" คำนวณจากความกว้างจริงของ label — ไม่ fix ที่ 42/36mm เหมือนเดิม
 * เพราะ label ภาษาไทยยาวกว่าอังกฤษมาก (เช่น 'Approved By:' → 'ผู้อนุมัติผ่านช่องทาง:') จะทับค่าที่ตามมา
 */
export function drawRow2(pdf: jsPDF, l1: string, v1: string, l2: string, v2: string, y: number): number {
  const W = 210, M = 15, cx = W / 2
  pdf.setFont(PDF_FONT, 'bold')
  pdf.setFontSize(8.5)
  const off1 = Math.max(42, pdf.getTextWidth(l1) + 4)
  const off2 = Math.max(36, pdf.getTextWidth(l2) + 4)
  pdf.text(l1, M + 2, y)
  pdf.setFont(PDF_FONT, 'normal')
  pdf.setFontSize(9.5)  // ค่าใหญ่กว่า label เล็กน้อย — ให้สัดส่วนใกล้เคียงรายงานส่งมอบ (label 9 / value 10)
  pdf.text(v1 || '—', M + off1, y, { maxWidth: cx - M - off1 - 2 })
  pdf.setFont(PDF_FONT, 'bold')
  pdf.setFontSize(8.5)
  pdf.text(l2, cx + 2, y)
  pdf.setFont(PDF_FONT, 'normal')
  pdf.setFontSize(9.5)
  pdf.text(v2 || '—', cx + off2, y, { maxWidth: cx - M - off2 - 2 })
  return y + 7.8
}

/** Draw single-column row — ระยะเยื้องคำนวณจาก label จริงเช่นเดียวกับ drawRow2 */
export function drawRow(pdf: jsPDF, label: string, value: string, y: number): number {
  const W = 210, M = 15
  pdf.setFont(PDF_FONT, 'bold')
  pdf.setFontSize(8.5)
  const off = Math.max(42, pdf.getTextWidth(label) + 4)
  pdf.text(label, M + 2, y)
  pdf.setFont(PDF_FONT, 'normal')
  pdf.setFontSize(9.5)
  pdf.text(value || '—', M + off, y, { maxWidth: W - M * 2 - off - 2 })
  return y + 7.8
}

/**
 * แถบหัวข้อ — สูงขึ้นและจัดกึ่งกลางแนวตั้งจริง เพื่อรองรับภาษาไทย
 *
 * ของเดิม: แถบสูง 7mm, fontSize 9 ตายตัว, baseline ที่ y+5
 * ภาษาไทยมีทั้งวรรณยุกต์ซ้อนบน (เช่น "อี" + ไม้โท) และสระล่าง (สระอุ/อู) ซึ่งกินพื้นที่เหนือ-ใต้
 * baseline มากกว่าอักษรละตินมาก พอเปลี่ยนหัวข้อเป็นไทยตัวอักษรจึงล้นออกนอกแถบสีทั้งบนและล่าง
 *
 * แก้เป็น: แถบสูง 8.5mm + คำนวณ baseline จากความสูงฟอนต์จริง (ไม่ fix ที่ +5)
 * + auto-fit ความกว้างด้วย fitFontSize() ที่มีอยู่แล้ว กันหัวข้อไทยยาวๆ ล้นขอบขวา
 */
export function drawSection(pdf: jsPDF, title: string, y: number, pageWidth: number = 210): number {
  const W = pageWidth, M = 15
  const BAR_H = 8.5
  const barW = W - M * 2
  pdf.setFillColor(226, 232, 240)
  pdf.rect(M, y, barW, BAR_H, 'F')

  // auto-fit ก่อน แล้วค่อยคำนวณ baseline จากขนาดที่ได้จริง
  const size = fitFontSize(pdf, title, barW - 8, 9, 6.5, 'bold')
  pdf.setFont(PDF_FONT, 'bold')
  pdf.setFontSize(size)
  pdf.setTextColor(30, 41, 59)
  // 1pt = 0.3528mm — วาง baseline ให้ตัวอักษรอยู่กึ่งกลางแถบ เผื่อที่ให้สระล่างเสมอ
  const baseline = y + BAR_H / 2 + (size * 0.3528) * 0.34
  pdf.text(title, M + 3, baseline)
  pdf.setTextColor(0, 0, 0)
  return y + BAR_H + 2.5
}

/**
 * เปิด PDF ที่สร้างเสร็จใน "แท็บใหม่ทุกครั้ง"
 *
 * ของเดิมทุกหน้าเรียก window.open(url, 'PrintPreview', '<features>') — ชื่อหน้าต่างซ้ำกันหมด
 * เบราว์เซอร์จึง reuse หน้าต่างเดิมเสมอ: พิมพ์ใบที่ 2 จะทับ preview ของใบแรก เทียบกันไม่ได้
 * ใช้ '_blank' แทน + ไม่ส่ง features string (ถ้าส่ง เบราว์เซอร์จะเปิดเป็น popup ไม่ใช่แท็บ)
 *
 * เผื่อกรณีถูก popup blocker บล็อก → fallback เป็นดาวน์โหลดไฟล์ตามเดิม
 */
export function openPdfInNewTab(pdf: jsPDF, filename: string): void {
  const url = pdf.output('bloburl') as unknown as string
  const win = window.open(url, '_blank')
  if (!win) pdf.save(filename)
}

/**
 * ฟังก์ชันแปลที่ส่งเข้าไปในตัวสร้างรายงาน
 * บังคับให้ต้องมี defaultValue เสมอ — ถ้าคีย์หายไปจาก th.json/en.json เอกสารจะยังอ่านออก
 * ไม่โผล่เป็นชื่อคีย์ดิบๆ เช่น "pdf.borrow.title" กลางหน้ากระดาษ
 * (เขียนเป็น type แคบๆ แทนการ import TFunction จาก i18next เพื่อไม่ผูก pdfHelper เข้ากับ i18next)
 */
export type PdfT = (key: string, defaultValue: string) => string

/** ข้อมูลการอนุมัติที่ backend บันทึกไว้ตอน approve (snapshot — ไม่ใช่ live lookup จาก profile) */
export interface ApprovalInfo {
  approvedBy?: string | null
  approvedAt?: string | Date | null
  approvedVia?: string | null       // 'line' | 'web' | 'email'
  approverSignature?: string | null // data URL PNG จาก SignaturePad
  approverPosition?: string | null
}

/**
 * ── ส่วน "ข้อมูลการอนุมัติ" สำหรับเอกสารฝั่ง jsPDF (Transfer / Borrow) ────────────
 * ทำให้เท่ากับที่ Handover (PDFKit, backend) แสดงอยู่แล้ว — ข้อมูลถูกบันทึกใน DB ครบตั้งแต่แรก
 * ขาดแค่การ render ฝั่งนี้เท่านั้น
 *
 * เงื่อนไขการแสดง: แสดงเฉพาะรายการที่ผ่านการอนุมัติจริงแล้ว (มี `approvedBy`) เท่านั้น
 * ถ้ายัง PENDING หรือถูก REJECTED จะคืน y เดิมโดยไม่วาดอะไรเลย — กันหัวข้อว่างเปล่าโผล่ในเอกสาร
 * (หมายเหตุ: reject ก็เซ็ต approvedBy เหมือนกันในบาง service ผู้เรียกจึงควรส่งเฉพาะตอน APPROVED
 *  ดูตัวอย่างการเรียกใน TransferPage/BorrowPage)
 *
 * ลายเซ็น: ถ้าผู้อนุมัติไม่เคยบันทึกลายเซ็นในโปรไฟล์ ก็เว้นว่างไว้ ส่วนที่เหลือแสดงตามปกติ
 * ต่างจากฝั่ง Handover ตรงที่คำนวณสัดส่วนจริงของรูปก่อนวาง (แทน fix 90x28 ตายตัว) — กันลายเซ็นบิดเบี้ยว
 * ใช้หลักการเดียวกับ logo ใน drawHeader()
 */
export async function drawApprovalSection(
  pdf: jsPDF,
  info: ApprovalInfo,
  y: number,
  opts: { lang?: 'en' | 'th'; sectionTitle?: string; showSignature?: boolean; labels?: Partial<Record<'section' | 'by' | 'via' | 'sig' | 'at', string>> } = {},
): Promise<number> {
  if (!info?.approvedBy) return y   // ← ไม่มีการอนุมัติ = ไม่แสดงหัวข้อนี้เลย

  const M = 15
  const lang = opts.lang ?? 'en'
  const showSignature = opts.showSignature !== false   // default = แสดง; Borrow ส่ง false มาปิด
  const L = {
    ...(lang === 'th'
      ? { section: 'ข้อมูลการอนุมัติ', by: 'ผู้อนุมัติ:', via: 'อนุมัติผ่าน:', sig: 'ลายเซ็นผู้อนุมัติ:', at: 'เมื่อ' }
      : { section: 'Approval Information', by: 'Approved By:', via: 'Approved Via:', sig: 'Signature:', at: 'on' }),
    ...(opts.labels ?? {}),
  }

  const CHANNEL: Record<string, string> = lang === 'th'
    ? { line: 'LINE', email: 'อีเมล', web: 'ระบบเว็บ' }
    : { line: 'LINE', email: 'Email', web: 'Web' }

  // ── กันหัวข้อถูกตัดครึ่งหน้า: จองพื้นที่เผื่อลายเซ็นไว้ล่วงหน้าแล้วค่อยตัดสินใจขึ้นหน้าใหม่ ──
  const SIG_BOX_H = 22
  const wantSig = showSignature && !!info.approverSignature
  // แถว "อนุมัติผ่าน" ต้องมีข้อมูลจริงอย่างน้อยหนึ่งอย่าง (via หรือ at) ถึงจะแสดง — เรคคอร์ดเก่าที่
  // อนุมัติไปก่อนฟีเจอร์ e-signature จะมี approvedBy แต่ approvedVia/approvedAt เป็นค่าว่างทั้งคู่
  // ถ้าแสดงแถวนี้ไปเลยจะได้ "อนุมัติผ่าน: — เมื่อ —" ซึ่งดูเป็นข้อผิดพลาด ไม่ใช่ข้อมูลที่ขาดหายจริง
  const wantViaRow = !!info.approvedVia || !!info.approvedAt
  const needed = 11 + 7 + (wantViaRow ? 7 : 0) + (wantSig ? SIG_BOX_H + 8 : 0)
  if (y + needed > pdf.internal.pageSize.height - 20) { pdf.addPage(); y = 20 }

  y = drawSection(pdf, opts.sectionTitle ?? L.section, y)

  const approvedAtStr = info.approvedAt
    ? new Date(info.approvedAt).toLocaleString(lang === 'th' ? 'th-TH' : 'en-GB', {
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—'

  y = drawRow(pdf, L.by, info.approverPosition ? `${info.approvedBy} (${info.approverPosition})` : info.approvedBy, y)
  if (wantViaRow) {
    // ประกอบทีละส่วนแทนการต่อสตริงตายตัว — ถ้ามีแค่ approvedAt (ไม่มี approvedVia) จะได้ไม่ขึ้นเป็น
    // "— เมื่อ ..." ที่มีขีดกลางค้างอยู่ข้างหน้าเฉยๆ ให้แสดงแค่ส่วนที่มีข้อมูลจริง
    const viaParts: string[] = []
    if (info.approvedVia) viaParts.push((CHANNEL[info.approvedVia] || info.approvedVia))
    if (info.approvedAt) viaParts.push(`${L.at} ${approvedAtStr}`)
    y = drawRow(pdf, L.via, viaParts.join(' '), y)
  }

  if (wantSig) {
    try {
      const SIG_BOX_W = 55
      const { w: natW, h: natH } = await getImageNaturalSize(info.approverSignature!)
      const scale = Math.min(SIG_BOX_W / natW, SIG_BOX_H / natH)
      const drawW = natW * scale
      const drawH = natH * scale

      pdf.setFont(PDF_FONT, 'bold')
      pdf.setFontSize(8.5)
      pdf.text(L.sig, M + 2, y)
      const sigOff = Math.max(42, pdf.getTextWidth(L.sig) + 4)
      pdf.addImage(info.approverSignature!, 'PNG', M + sigOff, y - 4, drawW, drawH, undefined, 'FAST')
      y += Math.max(drawH, 8) + 4
    } catch (_) {
      // โหลด/วางรูปไม่สำเร็จ = ข้ามลายเซ็นไป ไม่ให้เอกสารทั้งใบพังเพราะรูปเดียว
    }
  }

  return y + 2
}

/** Draw signature boxes */
export function drawSignatures(pdf: jsPDF, sigs: { title: string; sub: string }[], y: number): number {
  const W = 210, M = 15
  const REF_COLS = 3 // ── ใช้ 3 คอลัมน์อ้างอิงเสมอ ไม่ว่าจะมีกี่ลายเซ็นจริงๆ — กัน 2 ลายเซ็นถูกยืดเต็มแถว
  // เดิม cols ผูกกับ sigs.length ตรงๆ ทำให้ 2 ลายเซ็นได้ช่องกว้างเกินจริง (ยืดเต็มหน้า) แทนที่จะ
  // ชิดซ้ายเหมือนเหลือช่องที่ 3 ว่างไว้ ──
  const sigW = (W - M * 2 - (REF_COLS - 1) * 5) / REF_COLS
  const sigH = 36
  const rows = Math.ceil(sigs.length / REF_COLS)

  for (let i = 0; i < sigs.length; i++) {
    const row = Math.floor(i / REF_COLS)
    const col = i % REF_COLS
    const sx = M + col * (sigW + 5)
    const sy = y + row * (sigH + 6)
    pdf.setDrawColor(150, 150, 150)
    pdf.rect(sx, sy, sigW, sigH)
    pdf.setFont(PDF_FONT, 'bold')
    pdf.setFontSize(7.5)
    pdf.text(sigs[i].title, sx + 2, sy + 6)
    pdf.setFont(PDF_FONT, 'normal')
    pdf.setFontSize(7)
    pdf.text(sigs[i].sub, sx + 2, sy + 10.5)
    pdf.setDrawColor(180, 180, 180)
    pdf.line(sx + 2, sy + 24, sx + sigW - 2, sy + 24)
    pdf.text('Signature', sx + 2, sy + 28)
    pdf.line(sx + 2, sy + 32, sx + sigW - 2, sy + 32)
    pdf.text('Name / Date', sx + 2, sy + 35.5)
  }
  return y + rows * (sigH + 6) + 4
}

/** Draw footer */
export function drawFooter(pdf: jsPDF, ref: string, date: string, generatedBy: string = 'Generated by AssetOS'): void {
  const W = 210, M = 15
  const fy = pdf.internal.pageSize.height - 10
  pdf.setDrawColor(200, 200, 200)
  pdf.line(M, fy - 4, W - M, fy - 4)
  // ── ห้ามใช้ 'italic' ──────────────────────────────────────────────────────
  // registerThaiFont() ลงทะเบียน Sarabun ไว้แค่ 'normal' กับ 'bold' เท่านั้น
  // ทดสอบกับ jsPDF ที่โปรเจกต์ใช้แล้ว: setFont('Sarabun','italic') **ไม่ throw**
  // แต่ fallback เงียบๆ ไปเป็น 'times/italic' ซึ่งไม่มีตัวอักษรไทยเลย
  // ตอนที่ footer ยังเป็นอังกฤษล้วนจึงไม่มีใครเห็นปัญหา — พอแปลเป็นไทยจะกลายเป็นช่องว่างทันที
  pdf.setFont(PDF_FONT, 'normal')
  pdf.setFontSize(7)
  pdf.setTextColor(150, 150, 150)
  pdf.text(`${generatedBy}  •  ${ref}  •  ${date}`, W / 2, fy, { align: 'center' })
  pdf.setTextColor(0, 0, 0)
}
